import { ENV } from "../utils";
import { Auth } from "./auth";

const authController = new Auth();

const AUTH_FAILURE_MESSAGES = new Set([
  "El token ha expirado.",
  "Token invalido.",
]);

function isAuthFailure(status, body) {
  if (
    status === 403 &&
    body?.msg === "La petición no tiene la cabecera de autenticación."
  ) {
    return true;
  }
  if (status === 400 && AUTH_FAILURE_MESSAGES.has(body?.msg)) {
    return true;
  }
  return false;
}

// Promesa compartida a nivel de módulo: si varias peticiones fallan por auth
// casi al mismo tiempo, todas esperan el mismo refresh en vez de disparar uno cada una.
let refreshPromise = null;

function refreshAccessTokenOnce() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = authController.getRefreshToken();
      if (!refreshToken) throw new Error("No refresh token available");
      const result = await authController.refreshAccessToken(refreshToken);
      const newAccessToken = result?.access ?? result?.accessToken;
      if (!newAccessToken) throw new Error("Refresh response missing access token");
      authController.setAccessToken(newAccessToken);
      return newAccessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function notifySessionExpired() {
  authController.removeTokens();
  window.dispatchEvent(new CustomEvent("auth:session-expired"));
}

/**
 * apiFetch(path, options)
 * - path: ruta relativa a ENV.BASE_API, sin slash inicial (ej. `${ENV.API_ROUTES.USER}/me`)
 * - options: opciones estándar de fetch (method, headers, body), más:
 *     - expectedStatus (número, default 200): status que se considera éxito.
 * Devuelve el JSON parseado en éxito. Lanza el JSON de error del backend (o un Error) en fallo,
 * igual que los `catch (error) { ... error.msg ... }` ya existentes en los componentes.
 */
export async function apiFetch(path, options = {}) {
  const { expectedStatus = 200, headers, ...rest } = options;
  const url = `${ENV.BASE_API}/${path}`;

  const doFetch = async () => {
    const accessToken = authController.getAccessToken();
    const finalHeaders = { ...headers };
    if (accessToken) {
      finalHeaders.Authorization = `Bearer ${accessToken}`;
    }
    const response = await fetch(url, { ...rest, headers: finalHeaders });
    const body = await response.json();
    return { response, body };
  };

  let { response, body } = await doFetch();

  if (isAuthFailure(response.status, body)) {
    try {
      await refreshAccessTokenOnce();
    } catch (refreshError) {
      notifySessionExpired();
      throw body;
    }
    ({ response, body } = await doFetch());
    if (isAuthFailure(response.status, body)) {
      notifySessionExpired();
      throw body;
    }
  }

  if (response.status !== expectedStatus) throw body;
  return body;
}
